import { supabase } from "@/lib/supabaseClient";
import type { DailyStudyLog } from "@/types/models";

/** 최근 N일 학습 로그 조회 (차트용) */
export async function getRecentStudyLogs(userId: string, days = 7): Promise<DailyStudyLog[]> {
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - days);

  const { data, error } = await supabase
    .from("daily_study_logs")
    .select("*")
    .eq("user_id", userId)
    .gte("study_date", fromDate.toISOString().split("T")[0])
    .order("study_date");

  if (error) {
    throw error;
  }
  return data;
}

/** 오늘 학습(시험 완료) 했는지 확인 */
export async function hasStudiedToday(userId: string): Promise<boolean> {
  const today = new Date().toISOString().split("T")[0];

  const { count, error } = await supabase
    .from("exams")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("status", "completed")
    .eq("exam_date", today);

  if (error) {
    return false;
  }

  return (count ?? 0) > 0;
}

/** 학습 스트릭 계산 (연속 학습일) */
export async function getStudyStreak(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from("daily_study_logs")
    .select("study_date")
    .eq("user_id", userId)
    .order("study_date", { ascending: false })
    .limit(365);

  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    return 0;
  }

  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < data.length; i++) {
    const logDate = new Date(data[i].study_date);
    logDate.setHours(0, 0, 0, 0);

    const expectedDate = new Date(today);
    expectedDate.setDate(expectedDate.getDate() - i);

    if (logDate.getTime() === expectedDate.getTime()) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

/** 전체 학습 통계 요약 */
export async function getStudySummary(userId: string) {
  const { data: exams, error: examsError } = await supabase
    .from("exams")
    .select("score")
    .eq("user_id", userId)
    .eq("exam_type", "daily")
    .eq("status", "completed");

  if (examsError) {
    throw examsError;
  }

  const completedTests = exams?.length ?? 0;
  const avgScore =
    completedTests > 0 ? Math.round(exams.reduce((sum, e) => sum + (e.score ?? 0), 0) / completedTests) : 0;

  const { count: wordsLearned, error: wordsError } = await supabase
    .from("user_word_progress")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("status", "known");

  if (wordsError) {
    throw wordsError;
  }

  return {
    completedTests,
    avgScore,
    wordsLearned: wordsLearned ?? 0,
  };
}
